import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { newPasswordSchema as resetPasswordSchema } from '@fixtyer/shared';
import { useAuthStore } from '@/stores/auth-store';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const API_URL = import.meta.env.VITE_API_URL || '';

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, [token]);

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: data.password,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to reset password');
      }

      // Auto-login after successful password reset
      if (responseData.user && responseData.accessToken && responseData.refreshToken) {
        useAuthStore.getState().setAuth(
          responseData.user,
          responseData.accessToken,
          responseData.refreshToken
        );
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-16rem)] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-bold text-brand-950">
            Set new password
          </h1>
          <p className="mt-2 text-surface-800">
            Your new password must be different from previously used passwords
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-brand-900/5 border border-surface-200 p-8">
          {error && (
            <div className="mb-6 p-4 bg-error-500/10 border border-error-500/20 rounded-xl text-error-600 text-sm">
              {error}
            </div>
          )}

          {!token ? (
            <div className="text-center">
              <p className="text-surface-800 mb-6">
                This password reset link is invalid or has expired.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block px-6 py-3 rounded-xl bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors"
              >
                Request new link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <Input
                label="New password"
                type="password"
                autoComplete="new-password"
                placeholder="Enter new password"
                error={errors.password?.message}
                {...register('password')}
              />

              <Input
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm new password"
                error={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />

              <div className="bg-surface-50 border border-surface-200 rounded-xl p-4">
                <p className="text-sm text-surface-800 font-medium mb-2">
                  Password requirements:
                </p>
                <ul className="text-sm text-surface-700 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="text-brand-600 mt-0.5">✓</span>
                    <span>At least 8 characters</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-brand-600 mt-0.5">✓</span>
                    <span>At least one uppercase letter</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-brand-600 mt-0.5">✓</span>
                    <span>At least one number</span>
                  </li>
                </ul>
              </div>

              <Button type="submit" fullWidth loading={isLoading}>
                Reset password
              </Button>
            </form>
          )}

          <div className="mt-6 text-center text-sm">
            <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
